import * as React from "react";
import { FormControl } from "react-bootstrap";
import styled from "styled-components";
import { decimalCalc } from "../../../../../../../../server/lib/aggregate/common";
import { AdCopySelectableButton } from "./selectable-button";
import { decimalPoint } from "../../../../../../lib/decimal-point";

const isNumber = (v: any): boolean => {
	return typeof v === "number";
};

const calc = (
	value: number,
	step: number | string,
	max?: number,
	min?: number,
	isPercent?: boolean,
): number | undefined => {
	if (!isNumber(value)) return undefined;

	const stemNum = Number(step);

	const clampedValue = Math.max(Math.min(value, max ?? Infinity), min ?? -Infinity);

	if (isPercent) {
		return decimalCalc(clampedValue * 100, Math.min(1 / stemNum, 10));
	}
	return decimalCalc(clampedValue, Math.min(1 / stemNum, 10));
};

const WrapArea = styled.div`
	position: relative;
	text-align: center;
	padding: 2px;
	border: 1px solid #ddd;
`;

const ButtonArea = styled.div`
	position: absolute;
	background-color: white;
	padding: 2px;
	top: -20px;
	left: 90px;
	background-color: white;
	z-index: 20;
	border: 1px solid #ddd;
`;

export type EditableTextProps = {
	value: number;
	initValue: number;
	onChange: (value?: number) => void;
	isPercent?: boolean;
	max?: number;
	min?: number;
	step?: number | string;
};

export const AdCopyEditableText = React.memo((props: EditableTextProps) => {
	const { value, initValue, isPercent, onChange, max, min = 0, step = "0.1" } = props;
	const [focus, setFocus] = React.useState<boolean>(false);
	const [target, setTarget] = React.useState<boolean>(false);

	const [viewValue, setViewValue] = React.useState<string>(
		value !== undefined ? calc(value, step, max, min, isPercent).toFixed(decimalPoint(step)) : "",
	);
	const onChangeValue = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const vv = e.target.value;
			const num = Number(vv);
			if (vv === "" || isNaN(num)) {
				setViewValue("");
				return onChange(undefined);
			}

			const numValue = calc(num, step, max, min);
			setViewValue(numValue == null || isNaN(numValue) ? "" : numValue.toString());

			const stemNum = Number(step);
			const roundValue = decimalCalc(numValue, Math.min(1 / stemNum, 10));
			const v = isPercent ? roundValue / 100 : roundValue;

			onChange(v);
		},
		[isPercent, max, min, onChange, step],
	);
	const onBlur = React.useCallback(() => {
		const num = Number(viewValue);
		setViewValue(viewValue !== "" || isNaN(num) ? num.toFixed(decimalPoint(step)) : "");
		setFocus(false);
	}, [viewValue, step]);
	const events = React.useMemo(
		() => [
			{
				label: "補整する",
				event: () => {
					setTarget(false);
					setFocus(true);
				},
			},
			{
				label: "初期値から変更しない",
				event: () => {
					setTarget(false);
					const v = isPercent ? initValue * 100 : initValue;
					setViewValue(v.toFixed(1));
					onChange(initValue);
				},
			},
		],
		[onChange, initValue, isPercent],
	);
	const onClick = React.useCallback(() => {
		if (!focus && !target) {
			setTarget(true);
		}
	}, [focus, target]);
	const style = React.useMemo(() => {
		if (viewValue === "") {
			return { backgroundColor: "#ddd" };
		}
		return {};
	}, [viewValue]);
	if (!focus) {
		return (
			<WrapArea onClick={onClick} style={style}>
				{target && (
					<ButtonArea>
						<AdCopySelectableButton events={events} onClose={() => setTarget(false)} />
					</ButtonArea>
				)}
				<>{viewValue || "-"}</>
			</WrapArea>
		);
	}
	return (
		<FormControl
			type={"number"}
			step={step}
			max={max}
			min={min}
			value={viewValue}
			onChange={onChangeValue}
			onBlur={onBlur}
		/>
	);
});
